import React from 'react'

import {isEqual, keyBy, sum} from 'es-toolkit'
import {FormikProps} from 'formik'

import {useAppSelector} from '../../hooks/redux'

import download from '../../../../commons/src/services/download'
import getCloudinaryUrl from '../../libs/cloudinary'
import {
  formatInfillQuoteType,
  formatModelDimensions,
  formatPrice,
  formatScaleFactor,
  formatTimeRange,
  formatToleranceQuoteType,
} from '../../libs/formatter'
import {getProductionOption, scaleModelDimensions} from '../../libs/order'
import * as internalApi from '../../services/api/internal'
import * as partnerApi from '../../services/api/partner'

import {Color, MaterialConfig} from '../../types'

import downloadIcon from '../../../../commons/src/assets/icons/download-box.svg'

import ActionBar from '../../../../commons/src/components/action-bar'
import CheckboxField from '../../../../commons/src/components/checkbox-field'
import CounterTag from '../../../../commons/src/components/counter-tag'
import Icon from '../../../../commons/src/components/icon'
import Image from '../../../../commons/src/components/image'
import ImageLightbox from '../../../../commons/src/components/image-lightbox'
import Link from '../../../../commons/src/components/link'
import LinkButton from '../../../../commons/src/components/link-button'
import MaterialImage from '../../../../commons/src/components/material-image'
import ModalHandler from '../../../../commons/src/components/modal-handler'
import Paragraph from '../../../../commons/src/components/paragraph'
import Stack from '../../../../commons/src/components/stack'
import Tooltip from '../../../../commons/src/components/tooltip'
import TooltipHandler from '../../../../commons/src/components/tooltip-handler'
import TruncatedLabel from '../../../../commons/src/components/truncated-label'
import ModelImage from '../../components/model-image'
import OrderTag from '../../components/order-tag'
import Table from '../../components/table'
import TableCell from '../../components/table/TableCell'
import TableCellGroup from '../../components/table/TableCellGroup'
import TableHeaderCell from '../../components/table/TableHeaderCell'
import TableHeaderCellGroup from '../../components/table/TableHeaderCellGroup'
import TableRow from '../../components/table/TableRow'

import {
  BackendModelAttachment,
  ModelId,
  Order,
  OrderId,
  Quote,
  QuoteDetailRowContent,
} from '../../order-types'
import {RefundRequestFormData} from '../modals/orders/request-refund'

const tableColumnWidths = {
  group: {
    model: 2,
    quantity: 0.75,
    scale: 0.75,
    dimensions: 2,
    material: 2,
    printingMethod: 1,
    leadTime: 1.5,
    price: 1,
  },
  originalPrice: 1,
}

type Props = {
  order: Order
  quotes: Quote[]
  minified?: boolean
  formik?: FormikProps<RefundRequestFormData>
  renderProductionDetails?: (
    orderId: OrderId,
    modelId: ModelId,
    note?: string,
    attachments?: BackendModelAttachment[]
  ) => React.ReactNode
}

const ModelTablePartial = ({
  order,
  quotes,
  renderProductionDetails,
  minified = false,
  formik,
}: Props) => {
  const printingMethods = useAppSelector(state => state.core.printingMethods)
  const materialConfigs = useAppSelector(state => state.core.materialConfigs)
  const colors = useAppSelector(state => state.core.colors)
  const isAdminMode = useAppSelector(state => state.auth.isAdminMode)

  const [lightbox, setLightbox] = React.useState<{imageUrls: string[]; imageIndex: number}>()

  const currency =
    order.amounts.cartItems?.currency ||
    order.amounts.shipping?.currency ||
    order.amounts.additionalCosts?.currency ||
    ''

  const tableRows = React.useMemo(() => {
    const modelsById = keyBy(order.models, model => model.modelId)

    const orderTableRows: QuoteDetailRowContent[] = quotes.map(quote => {
      const model = modelsById[quote.modelId]
      const productionOption = getProductionOption(quote)
      const scaledModelDimensions = scaleModelDimensions(model.dimensions, quote.scale)

      // MaterialConfig may not exist anymore in the material catalogue
      const materialConfig: MaterialConfig | undefined = materialConfigs[quote.materialConfigId]
      const colorId = materialConfig?.colorId
      // Color may not exist anymore in the material catalogue
      const color: Color | undefined = colors[colorId]

      return {
        quoteId: quote.quoteId,
        modelThumbnailUrl: model.thumbnailUrl,
        modelId: model.modelId,
        fileName: model.fileName,
        quantity: quote.quantity,
        scale: quote.scale,
        dimensions: formatModelDimensions(scaledModelDimensions, model.fileUnit),
        material: quote.vendorMaterialName || quote.materialName,
        colorHexCode: color?.hexCode,
        colorImage: color?.image,
        printingMethod:
          quote.printingMethodId && printingMethods[quote.printingMethodId]
            ? printingMethods[quote.printingMethodId].name
            : '-',
        infill: quote.types && formatInfillQuoteType(quote.types),
        tolerance: quote.types && formatToleranceQuoteType(quote.types),
        leadTime: formatTimeRange(quote.productionTimeFast, quote.productionTimeSlow),
        productionOption,
        types: quote.types || [],
        price: formatPrice(quote.amounts.netPrice, currency, 2),
        originalPrice: formatPrice(
          quote.amounts.originalPrice || quote.vendorProductionPrice,
          currency,
          2
        ),
        note: quote.note,
        attachments: model.attachments,
        cncAnalysis: model.cncAnalysis,
      }
    })

    return orderTableRows
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order])

  const handleModelDownload = (modelIds: ModelId[]) => {
    const api = isAdminMode ? internalApi : partnerApi
    Promise.all(
      modelIds.map(modelId =>
        api.getModelDownload(order.orderId, modelId).then(response => response.url)
      )
    ).then(urls => download(...urls))
  }

  const renderQuoteRow = (row: QuoteDetailRowContent, index: number) => {
    const hasProductionDetails = row.note || (row.attachments ?? []).length > 0

    return (
      <TableRow
        key={row.quoteId}
        subRows={
          !minified && hasProductionDetails && renderProductionDetails ? (
            <TableRow>
              <TableCell subRow>
                {renderProductionDetails(order.orderId, row.modelId, row.note, row.attachments)}
              </TableCell>
            </TableRow>
          ) : undefined
        }
      >
        <TableCellGroup>
          {!!formik && (
            <TableCell width="18px" align="right">
              <CheckboxField
                checked={
                  formik.values.quotes.findIndex(
                    q => q.quoteId === row.quoteId && isEqual(q.types, row.types)
                  ) > -1
                }
                onChange={value => {
                  const rowQuote = {
                    quoteId: row.quoteId,
                    types: row.types || [],
                  }
                  if (value) {
                    formik.setFieldValue('quotes', [...formik.values.quotes, rowQuote])
                  } else {
                    formik.setFieldValue(
                      'quotes',
                      formik.values.quotes.filter(refundQuote => !isEqual(refundQuote, rowQuote))
                    )
                  }
                }}
                disabled={formik.isSubmitting}
              />
            </TableCell>
          )}
          <TableCell width={tableColumnWidths.group.model}>
            <Stack space="80">
              <ActionBar space="80" noWrap>
                <ModalHandler
                  lightbox
                  modal={({close}) => (
                    <ImageLightbox
                      onClose={() => {
                        setLightbox(undefined)
                        close()
                      }}
                      defaultImageIndex={lightbox?.imageIndex}
                    >
                      {(lightbox?.imageUrls ?? []).map(url => (
                        <Image key={url} src={url} alt="" fit="contain" />
                      ))}
                    </ImageLightbox>
                  )}
                >
                  {({open}) => (
                    <ModelImage
                      source={row.modelThumbnailUrl}
                      size="s"
                      alt={`Preview image of model ${row.fileName}`}
                      onClick={() => {
                        setLightbox({
                          imageUrls: tableRows.map(a => a.modelThumbnailUrl),
                          imageIndex: index,
                        })
                        open()
                      }}
                    />
                  )}
                </ModalHandler>
                <TooltipHandler
                  delayOnHover={500}
                  tooltip={<Tooltip appearance="dark">{row.fileName}</Tooltip>}
                >
                  <TruncatedLabel
                    postfix={
                      minified ? undefined : (
                        <LinkButton
                          icon={<Icon source={downloadIcon} />}
                          onClick={() => handleModelDownload([row.modelId])}
                        />
                      )
                    }
                  >
                    {minified ? (
                      row.fileName
                    ) : (
                      <Link onClick={() => handleModelDownload([row.modelId])}>
                        <strong>{row.fileName}</strong>
                      </Link>
                    )}
                  </TruncatedLabel>
                </TooltipHandler>
              </ActionBar>
            </Stack>
          </TableCell>
          <TableCell width={tableColumnWidths.group.quantity}>
            <CounterTag>{row.quantity}</CounterTag>
          </TableCell>
          <TableCell width={tableColumnWidths.group.scale}>
            <ActionBar space="80">
              <span>{formatScaleFactor(row.scale)}</span>
              {row.scale !== 1 && <OrderTag type="needsScaling" />}
            </ActionBar>
          </TableCell>
          {!minified && (
            <TableCell width={tableColumnWidths.group.dimensions}>{row.dimensions}</TableCell>
          )}
          <TableCell width={tableColumnWidths.group.material}>
            {row.material}{' '}
            {(row.colorHexCode || row.colorImage) && (
              <MaterialImage
                className="u-margin-left-xs"
                size="xs"
                square
                border
                image={
                  row.colorImage && getCloudinaryUrl(row.colorImage, ['w_700', 'h_458', 'c_fill'])
                }
                color={row.colorHexCode}
              />
            )}
            {row.infill && (
              <ActionBar space="70">
                <OrderTag type="infill" /> {row.infill}
              </ActionBar>
            )}
            {row.tolerance && (
              <ActionBar space="70">
                <OrderTag type="tolerance" /> {row.tolerance}
              </ActionBar>
            )}
          </TableCell>
          {!minified && (
            <TableCell width={tableColumnWidths.group.printingMethod}>
              {row.printingMethod}
              {row.printingMethod === 'CNC' && row.cncAnalysis && (
                <Stack>
                  <Paragraph size="xs" minor>
                    Axis tier: {row.cncAnalysis.axisRequirement || 'all'}
                    <br />
                    Setup count: {row.cncAnalysis.setupCount}
                  </Paragraph>
                </Stack>
              )}
            </TableCell>
          )}
          <TableCell width={tableColumnWidths.group.leadTime}>
            <ActionBar space="80" noWrap>
              <span>{row.leadTime} </span>
              {!!row.productionOption && <OrderTag type={row.productionOption} />}
            </ActionBar>
          </TableCell>
          <TableCell align="right" width={tableColumnWidths.group.price}>
            {row.price}
          </TableCell>
        </TableCellGroup>
        <TableCell align="right" width={tableColumnWidths.originalPrice}>
          {row.originalPrice}
        </TableCell>
      </TableRow>
    )
  }

  return (
    <Table
      header={
        <TableRow>
          <TableHeaderCellGroup width={sum(Object.values(tableColumnWidths.group))}>
            {formik && (
              <TableHeaderCell width="18px" align="right">
                <CheckboxField
                  checked={formik.values.quotes.length === tableRows.length}
                  onChange={() => {
                    if (formik.values.quotes.length > 0) {
                      formik.setFieldValue('quotes', [])
                    } else {
                      formik.setFieldValue(
                        'quotes',
                        tableRows.map(row => {
                          return {
                            quoteId: row.quoteId,
                            types: row.types || [],
                          }
                        })
                      )
                    }
                  }}
                  disabled={formik.isSubmitting}
                />
              </TableHeaderCell>
            )}
            <TableHeaderCell width={tableColumnWidths.group.model}>Model</TableHeaderCell>
            <TableHeaderCell width={tableColumnWidths.group.quantity}>Quantity</TableHeaderCell>
            <TableHeaderCell width={tableColumnWidths.group.scale}>Scale Factor</TableHeaderCell>
            {!minified && (
              <TableHeaderCell width={tableColumnWidths.group.dimensions}>
                Target Dimensions
              </TableHeaderCell>
            )}
            <TableHeaderCell width={tableColumnWidths.group.material}>Material</TableHeaderCell>
            {!minified && (
              <TableHeaderCell width={tableColumnWidths.group.printingMethod}>
                Manufacturing process
              </TableHeaderCell>
            )}
            <TableHeaderCell width={tableColumnWidths.group.leadTime}>Lead Time</TableHeaderCell>
            <TableHeaderCell width={tableColumnWidths.group.price} align="right">
              Customer price
            </TableHeaderCell>
          </TableHeaderCellGroup>
          <TableHeaderCell width={tableColumnWidths.originalPrice} align="right">
            Your price
          </TableHeaderCell>
        </TableRow>
      }
    >
      {tableRows.map(renderQuoteRow)}
      {!minified && order.amounts.cartItems.productionFee && (
        <TableRow>
          <TableCell>Min. production cost difference</TableCell>
          <TableCell>{formatPrice(order.amounts.cartItems.productionFee, currency, 2)}</TableCell>
        </TableRow>
      )}
    </Table>
  )
}

export default ModelTablePartial
